import { cloneElement, HTMLAttributes, ReactElement } from "react"
import { useHydrated } from "@/hooks/use-hydrated"
import { createPortal } from "react-dom"

type PortalContainer = Element | DocumentFragment | null

type PortalRenderProp =
  | ReactElement
  | ((props: HTMLAttributes<HTMLDivElement>) => ReactElement)

export interface PortalProps extends HTMLAttributes<HTMLDivElement> {
  container?: PortalContainer
  render?: PortalRenderProp
}

export function Portal({
  children,
  container: containerProp,
  render,
  ...props
}: PortalProps) {
  // Server HTML has no portal to hydrate into. A part of the page that
  // hydrates after the rest has mounted finds its container, so it must still
  // render nothing until hydration is over; React re-renders it right after.
  const hydrated = useHydrated()
  if (!hydrated) {
    return null
  }
  const container = containerProp || document.body

  const portalProps: HTMLAttributes<HTMLDivElement> = {
    ...props,
    children,
  }

  const content =
    render === undefined ? (
      <div {...portalProps} />
    ) : typeof render === "function" ? (
      render(portalProps)
    ) : (
      cloneElement(render, portalProps, children)
    )

  return createPortal(content, container)
}
